import { formatDate } from './format';

/**
 * Print refusals from `POST /qr-code/print/:qrId` and `/print-batch/:invoiceId`
 * (#1746, server half in #1759). A wristband is refused OUTRIGHT — nothing was
 * sent to the printer and nothing was marked printed:
 *
 * - 409 TICKET_NOT_ACTIVE `{ ticketStatus }` — the ticket was cancelled or
 *   refunded (a refund sets `QRCode.status = INACTIVE`).
 * - 409 WRONG_VISIT_DATE `{ visitDate, today }` — the ticket is for another
 *   visit day (Asia/Jakarta civil day); it used to print here and get turned
 *   away an hour later at the gate as `406 NOT_VALID_TODAY`.
 *
 * Neither is a printer problem, so neither may be shown as "gagal dicetak —
 * cek printer" and neither may offer a retry: the refusal is about the TICKET
 * and will stand for every further attempt. The copy below says what the
 * cashier should DO instead.
 *
 * A refusal travels two different ways, which is why the mapping takes the code
 * explicitly rather than only an error object:
 *
 * - single print → thrown as an {@link ApiError} (`errorCode` + `payload`);
 * - batch print → a result ROW with `status: "failed"` + `errorCode`, because
 *   the BO (`use-orders.ts`) and the kiosk (`printing-progress.js`) only
 *   understand success / failed / already_printed. A mixed-date invoice
 *   therefore carries a reason PER TICKET, not one blanket error.
 *
 * As in `visitDateErrors.ts`, callers branch on `errorCode` and never on the
 * status or the ticket's own status alone: a 409 from this endpoint can equally
 * be `ALREADY_PRINTED` (a reprint, not a refusal), and a `FAILED` row can
 * equally be a printer jam.
 */
export const TICKET_NOT_ACTIVE = 'TICKET_NOT_ACTIVE';
export const WRONG_VISIT_DATE = 'WRONG_VISIT_DATE';

/** The codes that mean "this ticket may not be printed at all". */
const PRINT_REFUSAL_CODES: readonly string[] = [TICKET_NOT_ACTIVE, WRONG_VISIT_DATE];

/** Is this a print refusal, as opposed to a printer/transport failure? */
export function isPrintRefusalCode(code: unknown): boolean {
  return typeof code === 'string' && PRINT_REFUSAL_CODES.includes(code);
}

/** The facts a refusal carries — flat on the error body and on a batch row. */
export interface PrintRefusalDetail {
  /** YYYY-MM-DD the ticket is actually for (WRONG_VISIT_DATE). */
  visitDate?: unknown;
  /** YYYY-MM-DD today, Asia/Jakarta (WRONG_VISIT_DATE). */
  today?: unknown;
  /** The QR's status, e.g. INACTIVE (TICKET_NOT_ACTIVE). */
  ticketStatus?: unknown;
}

/** Structural view of an {@link ApiError} — accepts any thrown value. */
function asErrorLike(err: unknown): { errorCode?: unknown; payload?: unknown; message?: unknown } {
  return err && typeof err === 'object'
    ? (err as { errorCode?: unknown; payload?: unknown; message?: unknown })
    : {};
}

const isIsoDay = (v: unknown): v is string =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * The visit day rescued from the server's own sentence, for a backend that
 * predates the structured `visitDate` field — its message names the ticket's
 * day first and today second ("… tanggal kunjungan 2026-10-25, bukan hari ini
 * (2026-10-20) …"). Best-effort: no match just means the date is not named.
 */
function visitDayFromMessage(message: unknown): string | null {
  if (typeof message !== 'string') return null;
  const found = message.match(/\d{4}-\d{2}-\d{2}/g);
  return found?.[0] ?? null;
}

/**
 * Cashier-facing Indonesian copy for one refusal, or null when `code` is not a
 * refusal (callers fall back to their generic handling, e.g. `err.message`).
 *
 * `detail` is preferred over `message`; `message` is only read to recover the
 * visit day from an older server. The visit date is NOT changed in the
 * ticketbox — the main-flow reschedule lives in the Back Office
 * (`/admin/sale/reschedule`) — so the copy points there rather than at a menu
 * this app does not have.
 */
export function printRefusalCopy(
  code: unknown,
  detail: PrintRefusalDetail = {},
  message?: unknown,
): string | null {
  if (!isPrintRefusalCode(code)) return null;

  if (code === TICKET_NOT_ACTIVE) {
    const status = typeof detail.ticketStatus === 'string' ? detail.ticketStatus.trim() : '';
    const which = status ? ` (status ${status})` : '';
    return (
      `Tiket ini sudah dibatalkan/refund${which} — gelang tidak dapat dicetak. ` +
      'Jika tamu tetap ingin masuk, buat transaksi baru.'
    );
  }

  const day = isIsoDay(detail.visitDate) ? detail.visitDate : visitDayFromMessage(message);
  const when = day ? `untuk kunjungan ${formatDate(day)}` : 'untuk tanggal kunjungan lain';
  return (
    `Tiket ini ${when}, bukan hari ini — gelang hanya dapat dicetak pada hari kunjungannya. ` +
    'Minta tamu datang pada tanggal tersebut, atau hubungi supervisor untuk mengubah tanggal ' +
    'kunjungan (jadwal ulang) di Back Office.'
  );
}

/**
 * A batch result, split so a refusal is never counted as a print failure.
 *
 * `attempted` excludes both the skipped rows and the refused ones, because the
 * cashier-facing ratio ("3/4 tiket dicetak. Cek printer.") is about what the
 * PRINTER was asked to do. A refused ticket was never offered to it, and
 * folding it into that ratio sends the cashier to inspect a printer that is
 * working perfectly.
 */
export interface PrintBatchSummary {
  attempted: number;
  printed: number;
  failed: number;
  refused: number;
  /** Distinct reasons — a MIXED invoice can hold more than one. */
  refusalReasons: string[];
}

/** Row shape this reads — structural, so `qrCode.api` can depend on this module. */
interface PrintResultRow {
  status?: string;
  message?: string;
}

export function summarizePrintBatch(rows: readonly PrintResultRow[]): PrintBatchSummary {
  const refusalReasons: string[] = [];
  let printed = 0;
  let failed = 0;
  let refused = 0;
  let attempted = 0;

  for (const row of rows) {
    if (row.status === 'REFUSED') {
      refused += 1;
      const reason = row.message?.trim();
      if (reason && !refusalReasons.includes(reason)) refusalReasons.push(reason);
      continue;
    }
    if (row.status === 'ALREADY_PRINTED' || row.status === 'NOT_PRINTED') continue;
    attempted += 1;
    if (row.status === 'PRINTED') printed += 1;
    else if (row.status === 'FAILED') failed += 1;
  }

  return { attempted, printed, failed, refused, refusalReasons };
}

/**
 * Indonesian copy for a thrown print refusal (single print), or null when the
 * error is not one.
 */
export function printRefusalMessage(err: unknown): string | null {
  const { errorCode, payload, message } = asErrorLike(err);
  const body = (payload && typeof payload === 'object' ? payload : {}) as PrintRefusalDetail & {
    data?: unknown;
  };
  // `toApiError` reads `errorCode` from `body.data ?? body`, so read the detail
  // from the same place: an envelope that nests the error under `data` keeps
  // the refusal's fields there too.
  const nested = (body.data && typeof body.data === 'object' ? body.data : {}) as PrintRefusalDetail;
  const detail: PrintRefusalDetail = {
    visitDate: nested.visitDate ?? body.visitDate,
    today: nested.today ?? body.today,
    ticketStatus: nested.ticketStatus ?? body.ticketStatus,
  };
  return printRefusalCopy(errorCode, detail, message);
}
